// app/javascript/controllers/ats_jd_match_controller.js
//
// Single controller instance per DOM region. The left-column
// accordion has targets: progressBar, rateLabel, missingKeywordsList,
// missingSection. The right-pane detail view has targets:
// keywordCount, matchCount, matchRate, jdCopy, resumeCopy,
// keywordList. apply(payload) uses hasXTarget guards so both work.
//
// IMPORTANT: the real API response does NOT include match_rate or
// missing_keywords — both are derived client-side here, mirroring the
// legacy updateMatchMetrics + flattenKeywords logic. The coordinator
// passes us `{ job_keywords, resume_text, jd_text }`; we flatten the
// categorized object, compare to the plain-text resume, and derive
// total / matches / rate / missing.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "progressBar", "rateLabel", "missingKeywordsList", "missingSection",
    "keywordCount", "matchCount", "matchRate",
    "jdCopy", "resumeCopy", "keywordList"
  ]

  apply(payload) {
    if (!payload) return
    const jobKeywords = payload.job_keywords || {}
    const resumeText  = (payload.resume_text || "").toLowerCase()
    const jdText      = payload.jd_text || ""

    const flat = Object.values(jobKeywords)
      .flatMap(v => Array.isArray(v) ? v : [])
      .filter(Boolean)
    const total   = flat.length
    const matched = flat.filter(k => resumeText.includes(String(k).toLowerCase()))
    const missing = flat.filter(k => !resumeText.includes(String(k).toLowerCase()))
    const rate    = total ? (matched.length / total) : 0
    const pct     = Math.round(rate * 100)

    // ── Left-column accordion ────────────────────────────────────────────
    if (this.hasRateLabelTarget)   this.rateLabelTarget.textContent = `${pct}%`
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${pct}%`
    if (this.hasMissingSectionTarget && this.hasMissingKeywordsListTarget) {
      this.missingSectionTarget.classList.toggle("hidden", missing.length === 0)
      this.missingKeywordsListTarget.replaceChildren(
        ...missing.map(k => this.#missingChip(k))
      )
    }

    // ── Right-pane detail ────────────────────────────────────────────────
    if (this.hasKeywordCountTarget) this.keywordCountTarget.textContent = total
    if (this.hasMatchCountTarget)   this.matchCountTarget.textContent   = matched.length
    if (this.hasMatchRateTarget)    this.matchRateTarget.textContent    = `${pct}%`
    if (this.hasJdCopyTarget)       this.jdCopyTarget.textContent       = jdText
    if (this.hasResumeCopyTarget)   this.resumeCopyTarget.textContent   = payload.resume_text || ""
    if (this.hasKeywordListTarget) {
      this.keywordListTarget.replaceChildren(
        ...flat.map(k => this.#keywordChip(k, matched.includes(k)))
      )
    }
  }

  #missingChip(term) {
    const span = document.createElement("span")
    span.className = "text-error font-medium"
    span.textContent = term
    return span
  }

  #keywordChip(term, present) {
    const span = document.createElement("span")
    span.className = present
      ? "px-2 py-0.5 bg-primary-fixed text-on-primary-fixed rounded text-[11px]"
      : "px-2 py-0.5 bg-error-container text-on-error-container rounded text-[11px]"
    span.textContent = term
    return span
  }
}
